import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Ticket } from 'lucide-react';
import { Button } from '../ui/button';
import { useReport } from '../../hooks/useDashboardReports';
import { paths } from '../../routes/paths';
import { EmptyReport, money, number, ReportPanel, ReportState } from './ReportPanel';

function RaffleProgress({ rifa }) {
  const report = useReport(`/api/tickets/porcentajeTicketsVendidos/${rifa._id}`);
  const raw = report.data?.vendidos;
  const percentage =
    raw != null && Number.isFinite(Number(raw)) ? Math.min(100, Math.max(0, Number(raw))) : null;
  return (
    <li className='py-4 first:pt-0 last:pb-0'>
      <div className='mb-3 flex items-center gap-3'>
        <div className='flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary'>
          <Ticket className='h-5 w-5' aria-hidden='true' />
        </div>
        <Link
          to={paths.admin.rifa(rifa._id)}
          className='min-w-0 flex-1 break-words text-sm font-medium text-foreground no-underline hover:text-primary focus-visible:outline-ring'
        >
          {rifa.nombre || 'Bono sin nombre'}
        </Link>
        <ArrowUpRight className='h-4 w-4 shrink-0 text-muted-foreground' aria-hidden='true' />
      </div>
      {report.state === 'loading' ? (
        <p role='status' className='mb-0 text-xs text-muted-foreground'>
          Cargando progreso…
        </p>
      ) : report.state === 'error' || percentage == null ? (
        <div className='flex items-center justify-between gap-2 text-xs text-muted-foreground'>
          <span>Progreso no disponible</span>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={report.retry}
            aria-label={`Reintentar progreso de ${rifa.nombre}`}
          >
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <div className='mb-2 flex justify-between text-xs text-muted-foreground'>
            <span>{rifa.tipo === 'board' ? 'Tablero' : 'Estándar'}</span>
            <span>{number(percentage)}% vendido</span>
          </div>
          <div
            className='h-2 overflow-hidden rounded-full bg-muted'
            role='progressbar'
            aria-label={`Tickets vendidos de ${rifa.nombre}`}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className='h-full rounded-full bg-primary' style={{ width: `${percentage}%` }} />
          </div>
        </>
      )}
    </li>
  );
}

export function RaffleProgressReport({ report }) {
  const rows = Array.isArray(report.data) ? report.data : report.data?.rifas || [];
  return (
    <ReportPanel
      title='Cada vez más cerca'
      description='Últimos cinco bonos · tickets vendidos sobre capacidad total, ahora.'
      action={
        <Button asChild variant='ghost' size='sm'>
          <Link to={paths.admin.rifas}>
            Ver bonos <ArrowUpRight aria-hidden='true' className='h-4 w-4' />
          </Link>
        </Button>
      }
    >
      <ReportState report={report} label='bonos'>
        {rows.length ? (
          <ul className='m-0 list-none divide-y divide-border p-0'>
            {rows.slice(0, 5).map(rifa => (
              <RaffleProgress key={rifa._id} rifa={rifa} />
            ))}
          </ul>
        ) : (
          <EmptyReport>
            No hay bonos todavía. Creá el primero desde las acciones rápidas.
          </EmptyReport>
        )}
      </ReportState>
    </ReportPanel>
  );
}

export function RaffleActivityReport({ rows = [], currency }) {
  return (
    <ReportPanel
      title='El detalle de tus bonos'
      description='Hasta 20 bonos ordenados por vistas. Vistas y selecciones cuentan eventos anónimos; sesiones cuenta sesiones únicas por bono. Ventas e ingresos netos provienen de la base de datos.'
    >
      {rows.length ? (
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[700px] text-sm'>
            <caption className='sr-only'>Actividad de bonos</caption>
            <thead>
              <tr className='border-b border-border text-muted-foreground'>
                {[
                  'Bono',
                  'Vistas',
                  'Sesiones',
                  'Selecciones',
                  'Ventas confirmadas',
                  'Ingresos netos',
                ].map((label, index) => (
                  <th
                    key={label}
                    scope='col'
                    className={`px-3 py-3 font-medium ${index ? 'text-right' : 'text-left'}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.rifaId}
                  className='border-b border-border/60 transition-colors last:border-0 hover:bg-muted/40'
                >
                  <th scope='row' className='max-w-xs break-words px-3 py-4 text-left font-medium'>
                    <Link
                      to={paths.admin.rifa(row.rifaId)}
                      className='text-foreground no-underline hover:text-primary'
                    >
                      {row.name}
                    </Link>
                  </th>
                  {[row.views, row.sessions, row.selections, row.sales].map((value, index) => (
                    <td key={index} className='px-3 py-4 text-right tabular-nums'>
                      {number(value)}
                    </td>
                  ))}
                  <td className='whitespace-nowrap px-3 py-4 text-right font-medium tabular-nums'>
                    {money(row.revenue, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyReport>Aún no hay actividad de bonos en este período.</EmptyReport>
      )}
    </ReportPanel>
  );
}
